import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { history } from '../../_utils';
import Utils, {
    matchesOperatorQuery,
    operatorName,
    operatorCode,
    shiftKindKey,
    isWorkShift,
    shiftHours,
    shiftTimes,
    coverageClass,
    holidayForDate,
    monthHourNorm,
    monthTileBar,
    minutesToTime,
    SHIFT_KINDS,
    buildShiftMap,
} from '../utils';

const kindByKey = SHIFT_KINDS.reduce((acc, item) => {
    acc[item.key] = item;
    return acc;
}, {});

const goToDay = (date) => {
    const formattedDate = moment(date).format('YYYY-MM-DD');
    try {
        const currentSearch = history.location.search;
        if (currentSearch) {
            sessionStorage.setItem('schedule_filter', currentSearch.replace(/^\?/, ''));
        }
    } catch (error) {
        console.error('Error saving filter:', error);
    }
    history.push(`/schedule/${formattedDate}/show`);
};

const formatDayTitle = (value) => {
    const m = moment(value);
    const dow = m.format('dd');
    return `${dow.charAt(0).toUpperCase()}${dow.slice(1)} ${m.format('D MMMM')}`;
};

const MonthTip = ({ tip }) => {
    if (!tip || !tip.shift) return null;
    const { shift, operator, date, x, y } = tip;
    const key = shiftKindKey(shift);
    const kind = kindByKey[key] || kindByKey.d;
    const { start, end } = shiftTimes(shift);
    const hours = shiftHours(shift);
    const below = y < 96;
    const left = Math.min(Math.max(148, x), (typeof window !== 'undefined' ? window.innerWidth : x) - 148);
    const statusLabel = key === 's' ? 'Больничный' : key === 'p' ? 'Прогул' : key === 'c' ? 'Отмена' : kind.n;
    return (
        <div
            className="pc-month-tip"
            style={{
                left,
                top: y,
                transform: below ? 'translate(-50%, 10px)' : 'translate(-50%, calc(-100% - 10px))',
            }}
        >
            <div className="k">
                <i style={{ background: kind.color }} />
                {statusLabel}
            </div>
            <div className="who">{operatorName(operator)}{operatorCode(operator) ? ` · ${operatorCode(operator)}` : ''}</div>
            <div className="when">{formatDayTitle(date)}</div>
            {key !== 's' && key !== 'p' ? (
                <div className="tm">{minutesToTime(start)} – {minutesToTime(end)}{hours ? ` · ${hours} ч` : ''}</div>
            ) : null}
            {shift.description ? <div className="note">{shift.description}</div> : null}
        </div>
    );
};

const MonthTile = ({ shift, dimmed, onClick, onTip, onTipHide }) => {
    if (!shift) {
        return (
            <button
                type="button"
                className="pc-month-add"
                onClick={(event) => { event.stopPropagation(); onClick && onClick(); }}
            >
                +
            </button>
        );
    }

    const key = shiftKindKey(shift);
    const { start, end } = shiftTimes(shift);
    const hourLabel = String(Math.floor((start || 0) / 60)).padStart(2, '0');
    const dim = dimmed ? ' dim' : '';
    const hover = {
        onMouseEnter: (event) => onTip && onTip(event),
        onMouseLeave: () => onTipHide && onTipHide(),
    };

    if (key === 's') {
        return (
            <button type="button" className={`pc-month-t t-s${dim}`} {...hover} onClick={(event) => { event.stopPropagation(); onClick && onClick(); }}>
                б
            </button>
        );
    }
    if (key === 'p') {
        return (
            <button type="button" className={`pc-month-t t-p${dim}`} {...hover} onClick={(event) => { event.stopPropagation(); onClick && onClick(); }}>
                <span className="n">п</span>
            </button>
        );
    }
    if (key === 'c') {
        return (
            <button type="button" className={`pc-month-t t-c${dim}`} {...hover} onClick={(event) => { event.stopPropagation(); onClick && onClick(); }}>
                <span className="n">{hourLabel}</span>
            </button>
        );
    }

    const bar = monthTileBar(start, end);
    return (
        <button type="button" className={`pc-month-t t-${key}${dim}`} {...hover} onClick={(event) => { event.stopPropagation(); onClick && onClick(); }}>
            <span className="n">{hourLabel}</span>
            <span className="b" style={bar} />
        </button>
    );
};

const MonthView = ({
    operators = [],
    schedule = [],
    dates = [],
    monthAnchor,
    holidays = [],
    planByDate = [],
    searchQuery = '',
    kindFilter = [],
    hideEmpty = true,
    onCellClick,
    onOperatorClick,
    onWeekClick,
}) => {
    const [unfolded, setUnfolded] = useState(false);
    const [tip, setTip] = useState(null);
    const monthDays = useMemo(
        () => (dates || []).filter((date) => moment(date).isSame(monthAnchor || dates[0], 'month')),
        [dates, monthAnchor]
    );

    useEffect(() => {
        setUnfolded(false);
        setTip(null);
    }, [hideEmpty, searchQuery, monthAnchor]);

    const shiftMap = useMemo(() => buildShiftMap(schedule), [schedule]);

    const filteredOperators = useMemo(
        () => (operators || []).filter((op) => matchesOperatorQuery(op, searchQuery)),
        [operators, searchQuery]
    );

    const rows = useMemo(() => {
        const withShifts = [];
        const empty = [];
        filteredOperators.forEach((operator) => {
            const id = operator.id || operator._id;
            const shifts = monthDays.map((date) => shiftMap[`${id}|${moment(date).format('YYYY-MM-DD')}`] || Utils.getShiftForDate(schedule, id, date));
            const hours = shifts.reduce((sum, shift) => sum + shiftHours(shift), 0);
            const days = shifts.filter(isWorkShift).length;
            const hasShift = shifts.some(Boolean);
            const row = { operator, shifts, hours, days, hasShift };
            if (hasShift) withShifts.push(row);
            else empty.push(row);
        });
        return { withShifts, empty };
    }, [filteredOperators, monthDays, shiftMap, schedule]);

    const planMap = useMemo(() => {
        const map = {};
        (dates || []).forEach((date, index) => {
            map[moment(date).format('YYYY-MM-DD')] = planByDate[index] || 0;
        });
        return map;
    }, [dates, planByDate]);

    const coverage = useMemo(
        () => monthDays.map((date) => {
            const dateStr = moment(date).format('YYYY-MM-DD');
            const fact = (schedule || []).reduce((count, shift) => {
                const shiftDate = moment((shift.schedules && shift.schedules[0] && shift.schedules[0].date) || shift.date).format('YYYY-MM-DD');
                if (shiftDate !== dateStr) return count;
                return count + (isWorkShift(shift) ? 1 : 0);
            }, 0);
            return { fact, plan: planMap[dateStr] || 0 };
        }),
        [monthDays, schedule, planMap]
    );

    const norm = useMemo(() => monthHourNorm(monthAnchor || monthDays[0], holidays), [monthAnchor, monthDays, holidays]);
    const foldEmpty = hideEmpty && !unfolded && rows.empty.length > 0 && rows.withShifts.length > 0;
    const visibleRows = foldEmpty ? rows.withShifts : rows.withShifts.concat(rows.empty);
    const today = moment().format('YYYY-MM-DD');
    const weekGroups = useMemo(() => {
        const groups = [];
        monthDays.forEach((date) => {
            const day = moment(date);
            const week = day.isoWeek();
            const key = `${day.isoWeekYear()}-W${week}`;
            const last = groups[groups.length - 1];
            if (last && last.key === key) {
                last.count += 1;
                return;
            }
            const weekStart = day.clone().startOf('isoWeek');
            const weekEnd = weekStart.clone().add(6, 'days');
            groups.push({
                key,
                week,
                count: 1,
                date: day.toDate(),
                title: `Неделя ${week}: ${weekStart.format('D MMM')} — ${weekEnd.format('D MMM')}`,
            });
        });
        return groups;
    }, [monthDays]);

    return (
        <div className="pc-month-wrap">
            <div className="pc-month-scroll" onScroll={() => setTip(null)}>
            <table className="pc-month-table">
                <thead>
                    <tr className="weeks">
                        <th className="namec">Нед.</th>
                        {weekGroups.map((group) => (
                            <th key={group.key} className="wk" colSpan={group.count}>
                                <button
                                    type="button"
                                    title={`${group.title}. Открыть неделю`}
                                    onClick={() => onWeekClick && onWeekClick(group.date)}
                                >
                                    {group.week}
                                </button>
                            </th>
                        ))}
                        <th className="sum" />
                    </tr>
                    <tr className="days">
                        <th className="namec">Сотрудник</th>
                        {monthDays.map((date) => {
                            const dateStr = moment(date).format('YYYY-MM-DD');
                            const holiday = holidayForDate(holidays, date);
                            const isWeekend = moment(date).isoWeekday() >= 6;
                            const isHoliday = holiday && holiday.type === 'holiday';
                            const isToday = dateStr === today;
                            const isMonday = moment(date).isoWeekday() === 1;
                            return (
                                <th
                                    key={dateStr}
                                    className={[
                                        isWeekend || isHoliday ? 'we' : '',
                                        isToday ? 'today' : '',
                                        isMonday ? 'wend' : '',
                                    ].filter(Boolean).join(' ')}
                                    onClick={() => goToDay(date)}
                                    title={holiday ? holiday.name : 'Открыть день'}
                                >
                                    <span className="wd">{moment(date).format('dd')}</span>
                                    <span className="dn">{moment(date).date()}</span>
                                </th>
                            );
                        })}
                        <th className="sum">Часы / норма</th>
                    </tr>
                    <tr className="cov">
                        <td className="namec">В смене</td>
                        {monthDays.map((date, index) => {
                            const dateStr = moment(date).format('YYYY-MM-DD');
                            const { fact, plan } = coverage[index] || { fact: 0, plan: 0 };
                            const cls = coverageClass(fact, plan);
                            const tone = cls === 'low' ? 'cov-w' : cls === 'crit' ? 'cov-b' : '';
                            const isMonday = moment(date).isoWeekday() === 1;
                            return (
                                <td
                                    key={dateStr}
                                    className={[tone, isMonday ? 'wend' : ''].filter(Boolean).join(' ')}
                                    title={plan ? `${fact} из ${plan} по плану` : `${fact} в смене`}
                                >
                                    {fact}
                                </td>
                            );
                        })}
                        <td className="sum" />
                    </tr>
                </thead>
                <tbody>
                    {visibleRows.map((row) => {
                        const id = row.operator.id || row.operator._id;
                        const pct = norm.hours ? Math.round((row.hours / norm.hours) * 100) : 0;
                        const st = pct < 92 ? 'under' : pct > 108 ? 'over' : 'ok';
                        return (
                            <tr key={id}>
                                <td className="namec">
                                    <button
                                        type="button"
                                        className="nm"
                                        title={operatorName(row.operator)}
                                        onClick={() => onOperatorClick && onOperatorClick(id)}
                                    >
                                        {operatorName(row.operator)}
                                    </button>
                                    <div className="cd">{operatorCode(row.operator)}{row.days ? ` · ${row.days} см` : ''}</div>
                                </td>
                                {monthDays.map((date, index) => {
                                    const dateStr = moment(date).format('YYYY-MM-DD');
                                    const shift = row.shifts[index];
                                    const key = shiftKindKey(shift);
                                    const dimmed = kindFilter.length > 0 && key && !kindFilter.includes(key);
                                    const holiday = holidayForDate(holidays, date);
                                    const isWeekend = moment(date).isoWeekday() >= 6;
                                    const isHoliday = holiday && holiday.type === 'holiday';
                                    const isToday = dateStr === today;
                                    const isMonday = moment(date).isoWeekday() === 1;
                                    return (
                                        <td
                                            key={dateStr}
                                            className={[
                                                'cell',
                                                isWeekend || isHoliday ? 'wecol' : '',
                                                isToday ? 'todaycol' : '',
                                                isMonday ? 'wend' : '',
                                            ].filter(Boolean).join(' ')}
                                        >
                                            <MonthTile
                                                shift={shift}
                                                dimmed={dimmed}
                                                onClick={() => onCellClick && onCellClick(row.operator, date, shift)}
                                                onTip={(event) => {
                                                    const rect = event.currentTarget.getBoundingClientRect();
                                                    setTip({
                                                        shift,
                                                        operator: row.operator,
                                                        date,
                                                        x: rect.left + rect.width / 2,
                                                        y: rect.top,
                                                    });
                                                }}
                                                onTipHide={() => setTip(null)}
                                            />
                                        </td>
                                    );
                                })}
                                <td className={`sum ${st}`}>
                                    <div className="v">{row.hours ? Math.round(row.hours) : 0}</div>
                                    <div className="p">из {norm.hours} · {pct}%</div>
                                    <div className="bar"><i style={{ width: `${Math.min(100, pct)}%` }} /></div>
                                </td>
                            </tr>
                        );
                    })}
                    {foldEmpty && (
                        <tr className="foldrow">
                            <td colSpan={monthDays.length + 2}>
                                <button type="button" onClick={() => setUnfolded(true)}>
                                    Ещё {rows.empty.length} без смен в этом месяце — показать
                                    <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7">
                                        <path d="M4 6.5L8 10.5l4-4" />
                                    </svg>
                                </button>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            </div>
            <div className="pc-month-hint">
                <span>В плитке — <b>час начала смены</b>, полоска снизу показывает положение смены в сутках</span>
                <span className="dot">·</span>
                <span>Наведите на плитку, чтобы увидеть точное время</span>
                <span className="norm">Норма {moment(monthAnchor || monthDays[0]).format('MMMM')} — <b>{norm.hours} ч</b> ({norm.days} рабочих дней)</span>
            </div>
            <MonthTip tip={tip} />
        </div>
    );
};

export default MonthView;
